import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import useScreen from 'hooks/useScreen';
import QuizPresenter from 'presenters/QuizPresenter';
import Icon from 'sharedComponents/Icon';
import { Box, Typography, IconButton, Button } from '@material-ui/core';
import { calculateQuizValues, calculateMonthlyTaxes } from 'utils/quizHelper';

import MainSummaryBar from './components/MainSummaryBar';
import MortgagePayment from './components/MortgagePayment';
import LoanPayments from './components/LoanPayments';
import MonthlyBudget from './components/MonthlyBudget';
import SavingsSituation from './components/SavingsSituation';

import useStyles from './useStyles';

const SummaryPage = props => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { isDesktop, isMobile } = useScreen();
  const { quizValues, onBack, onStartOver, onSave, onCompare } = props;
  const summary = calculateQuizValues(quizValues);
  const {
    houseValue,
    houseValueDp,
    pni,
    mortgagePayment,
    propTaxes,
    mortIns,
    hazIns,
    incomeValue,
    monthlyIncome,
    totalDebtPayments,
    carPaymentValue,
    studentPaymentValue,
    cardPaymentValue,
    otherPaymentValue,
    savingsValue,
    equityValue,
    houseValueCc,
  } = summary;

  const monthlyTaxes = calculateMonthlyTaxes(QuizPresenter.hasSecondPerson(quizValues), incomeValue);
  const taxesPercent = (monthlyTaxes / monthlyIncome) * 100;
  const savings = monthlyIncome * 0.1;
  const currentHouse = QuizPresenter.currentHouse(quizValues);

  const annualIncomeRatio = houseValue / incomeValue;
  const mortgageLoan = houseValue - houseValueDp;

  const loanPayments = totalDebtPayments + mortgagePayment;
  const loanPaymentsPercent = (loanPayments / monthlyIncome) * 100;
  const otherExpenses = (incomeValue / 12) * 0.9 - monthlyTaxes - totalDebtPayments - mortgagePayment;
  const otherExpensesPercent = (otherExpenses / monthlyIncome) * 100;

  const availableForNewHouse = savingsValue + equityValue;
  const afterHomePurchase = savingsValue + equityValue - houseValueDp - houseValueCc;
  const monthsSavings = (savingsValue + equityValue - houseValueDp - houseValueCc) / (incomeValue / 12);

  return (
    <Box width="100%" height="100%" display="flex" flexDirection="column" alignItems="center">
      <Box className={classes.paper}>
        {isDesktop && (
          <Box my={4}>
            <Typography variant="h1">{t('components.Quiz.SummaryPage.header')}</Typography>
          </Box>
        )}
        {isMobile && (
          <Box className={classes.header}>
            <IconButton onClick={onBack} className={classes.backButton}>
              <Icon name="backArrow" />
            </IconButton>
            <Typography variant="h2">{t('components.Quiz.SummaryPage.report')}</Typography>
          </Box>
        )}
        <Box className={classes.container}>
          <MainSummaryBar
            homePrice={houseValue}
            annualIncomeRatio={annualIncomeRatio}
            downPayment={houseValueDp}
            mortgageLoan={mortgageLoan}
          />
          <Box display="flex" width="100%" flexWrap="wrap">
            <MortgagePayment
              interest={pni}
              taxes={propTaxes}
              mortgageInsurance={mortIns}
              hazardInsurance={hazIns}
              mortgagePayment={mortgagePayment}
            />
            <LoanPayments
              carPayment={carPaymentValue}
              studentLoanPayment={studentPaymentValue}
              mortgagePayment={mortgagePayment}
              creditCardPayment={cardPaymentValue}
              othelLoansPayment={otherPaymentValue}
              loanPayments={loanPayments}
            />
            <MonthlyBudget
              income={monthlyIncome}
              incomePercent={100}
              taxes={monthlyTaxes}
              taxesPercent={taxesPercent}
              savings={savings}
              savingsPercent={10}
              loanPayments={loanPayments}
              loanPaymentsPercents={loanPaymentsPercent}
              otherExpenses={otherExpenses}
              otherExpensesPercent={otherExpensesPercent}
            />
            <SavingsSituation
              before={savingsValue}
              equity={equityValue}
              available={availableForNewHouse}
              after={afterHomePurchase}
              downPayment={houseValueDp}
              closingCost={houseValueCc}
              monthsSavings={monthsSavings}
              hasHouse={currentHouse}
            />
          </Box>
        </Box>
        <Box className={classes.controlBar}>
          {isDesktop && (
            <Button
              className={classes.marginRight}
              color="secondary"
              startIcon={<Icon name="backArrow" />}
              onClick={onBack}
            >
              {t('components.Quiz.SummaryPage.back')}
            </Button>
          )}
          <Box display="flex" justifyContent="space-between" width="100%">
            <Button color="secondary" startIcon={<Icon name="startOver" />} onClick={onStartOver}>
              {t('components.Quiz.SummaryPage.startOver')}
            </Button>
            <Button color="secondary" startIcon={<Icon name="save" />} onClick={onSave}>
              {t('components.Quiz.SummaryPage.save')}
            </Button>
          </Box>
          <Button className={classes.compareButton} startIcon={<Icon name="compare" />} onClick={onCompare}>
            {t('components.Quiz.SummaryPage.compare')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

SummaryPage.propTypes = {
  quizValues: QuizPresenter.shape().isRequired,
  onBack: PropTypes.func.isRequired,
  onStartOver: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onCompare: PropTypes.func.isRequired,
};

export default SummaryPage;



// WEBPACK FOOTER //
// ./src/components/QuizPage/components/SummaryPage/SummaryPage.js